import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  SITE_NAME,
  SITE_URL,
  DEFAULT_OG_IMAGE,
  ORGANIZATION,
  absoluteUrl,
  shouldNoIndex
} from '../../config/seo';

/**
 * Reusable SEO head manager — title, meta, Open Graph, Twitter, canonical, JSON-LD.
 */
function Seo({
  title,
  description,
  keywords,
  path,
  image,
  type = 'website',
  noindex = false,
  jsonLd = null,
  children
}) {
  const pathname = path || (typeof window !== 'undefined' ? window.location.pathname : '/');
  const canonical = absoluteUrl(pathname);
  const ogImage = image
    ? (image.startsWith('http') ? image : absoluteUrl(image))
    : DEFAULT_OG_IMAGE;
  const fullTitle = title?.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const isNoIndex = noindex || shouldNoIndex(pathname);

  const schemas = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  const defaultOrgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: ORGANIZATION.name,
    url: ORGANIZATION.url,
    logo: ORGANIZATION.logo,
    email: ORGANIZATION.email,
    telephone: ORGANIZATION.phone,
    address: {
      '@type': 'PostalAddress',
      addressCountry: ORGANIZATION.address.addressCountry,
      addressLocality: ORGANIZATION.address.addressLocality
    }
  };

  const allSchemas = [defaultOrgSchema, ...schemas];

  return (
    <Helmet prioritizeSeoTags>
      <html lang="en" />
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      {isNoIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage.startsWith('http') ? ogImage : absoluteUrl(ogImage)} />
      <meta property="og:locale" content="en_KE" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImage.startsWith('http') ? ogImage : absoluteUrl(ogImage)} />

      {/* Geo / local relevance for Kenya & Africa */}
      <meta name="geo.region" content="KE" />
      <meta name="geo.placename" content="Nairobi, Kenya" />

      {allSchemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
      {children}
    </Helmet>
  );
}

/** WebSite schema with sitelinks search box — helps Google understand site search */
export function buildWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/shop/search?q={search_term_string}`
      },
      'query-input': 'required name=search_term_string'
    }
  };
}

/** SoftwareApplication schema for ChesiWMS on homepage */
export function buildWmsSoftwareSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'ChesiWMS',
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web, Cloud',
    description:
      'Cloud warehouse management system (WMS) for inventory control, picking, receiving, shipping and barcode integration. Built for African warehouses and distribution centers.',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'KES',
      description: 'Contact for pricing'
    },
    provider: {
      '@type': 'Organization',
      name: ORGANIZATION.name,
      url: ORGANIZATION.url
    },
    featureList: [
      'Inventory management',
      'Barcode scanning integration',
      'Order picking and packing',
      'Multi-warehouse support',
      'Real-time stock tracking',
      'AIDC device integration'
    ]
  };
}

export function buildProductSchema(product, canonicalUrl) {
  const price = Number(product?.discount || product?.price || 0);
  const inStock = Number(product?.stock_quantity || 0) > 0;
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    sku: product.sku,
    description: product.short_description || product.long_description,
    brand: product.brand_name
      ? { '@type': 'Brand', name: product.brand_name }
      : undefined,
    image: product.primary_image_url || product.images?.[0]?.image_url,
    url: canonicalUrl,
    offers: {
      '@type': 'Offer',
      url: canonicalUrl,
      priceCurrency: 'KES',
      price: price > 0 ? price.toFixed(2) : undefined,
      availability: inStock
        ? 'https://schema.org/InStock'
        : 'https://schema.org/OutOfStock',
      seller: { '@type': 'Organization', name: ORGANIZATION.name }
    }
  };
}

export function buildBreadcrumbSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url ? absoluteUrl(item.url) : undefined
    }))
  };
}

export function buildItemListSchema(items, listName) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: listName,
    numberOfItems: items.length,
    itemListElement: items.slice(0, 20).map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: absoluteUrl(`/shop/product/${item.slug || item.id}`),
      name: item.name
    }))
  };
}

export default Seo;
